/**
 * Admin Preview — Direction 2 (Construction Cockpit, DARK)
 *
 * Mission-control aesthetic. Dark surfaces, hi-vis orange, condensed
 * industrial type. Icon-only sidebar rail + flyout panel so 30+ items
 * never overwhelm the rail.
 *
 * Self-contained mockup with static data. Reach it at /admin-preview-2.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  LayoutDashboard, Inbox as InboxIcon, MessageSquare,
  HardHat, ShieldAlert, Hammer, FolderKanban, Trophy,
  Receipt, Truck, Settings, Search, Bell, ArrowUpRight,
  ArrowLeft, AlertTriangle, Sun, Wind, Cloud, CheckCircle2,
  Activity, Zap, Circle, ShieldCheck, MapPin, Camera,
  Clock, FileText, Users,
} from "lucide-react";

// --- design tokens ---
const COL = {
  bg: "#0A0A0A",
  surface: "#141414",
  surfaceHi: "#1C1C1C",
  border: "#262626",
  borderHi: "#383838",
  brand: "#FF5F15",
  brandGlow: "rgba(255, 95, 21, 0.35)",
  brandSoft: "rgba(255, 95, 21, 0.10)",
  text: "#FAFAFA",
  textMuted: "#A3A3A3",
  textDim: "#525252",
  ok: "#22C55E",
  warn: "#F59E0B",
  danger: "#EF4444",
};

const fontHead = { fontFamily: "'Barlow Condensed', system-ui, sans-serif", fontWeight: 800, letterSpacing: "0.01em" };
const fontBody = { fontFamily: "'Work Sans', system-ui, sans-serif" };
const fontMono = { fontFamily: "'IBM Plex Mono', ui-monospace, monospace" };

// Icon-only rail (always visible). Tap a section → flyout panel on the right.
const RAIL = [
  { id: "overview", icon: LayoutDashboard, label: "Overview", badge: null, active: true },
  { id: "inbox", icon: InboxIcon, label: "Inbox", badge: 4 },
  { id: "chat", icon: MessageSquare, label: "Chat", badge: 12 },
  { id: "people", icon: HardHat, label: "People" },
  { id: "safety", icon: ShieldAlert, label: "Safety" },
  { id: "work", icon: Hammer, label: "Work" },
  { id: "pm", icon: FolderKanban, label: "Project Management" },
  { id: "crew", icon: Trophy, label: "Crew" },
  { id: "sales", icon: Receipt, label: "Sales" },
  { id: "assets", icon: Truck, label: "Assets" },
  { id: "account", icon: Settings, label: "Account" },
];

const FLYOUT_ITEMS = {
  people: ["Timesheets", "Leave", "Payroll", "Team", "Competencies"],
  safety: ["Safety", "Toolbox", "Pre-Starts", "SSSPs", "Forms"],
  work: ["Jobs", "Variations", "Dispatch", "Clients", "Suppliers"],
  pm: ["Project control"],
  crew: ["This week's numbers", "Team pulse"],
  sales: ["Quotes", "Customer invoices", "Rate library", "Supplier bills"],
  assets: ["Fleet", "Live tracking", "Gear", "Materials"],
  account: ["Branding", "Billing", "Help centre", "Support", "What's new"],
};

export default function AdminPreview2() {
  const [hover, setHover] = useState(null);

  return (
    <div
      className="min-h-screen flex"
      style={{ ...fontBody, background: COL.bg, color: COL.text }}
      data-testid="admin-preview-2"
    >
      {/* ========== ICON RAIL ========== */}
      <aside
        className="w-16 shrink-0 flex flex-col items-center border-r"
        style={{ borderColor: COL.border, background: COL.surface }}
        onMouseLeave={() => setHover(null)}
      >
        {/* logo */}
        <div className="h-16 w-full flex items-center justify-center border-b" style={{ borderColor: COL.border }}>
          <div
            className="w-8 h-8 rounded-sm flex items-center justify-center"
            style={{ background: COL.brand, boxShadow: `0 0 18px ${COL.brandGlow}` }}
          >
            <span className="text-white text-xs" style={fontHead}>FL</span>
          </div>
        </div>

        <div className="flex-1 flex flex-col items-center py-3 gap-1 w-full">
          {RAIL.map((it) => (
            <RailItem key={it.id} {...it} onHover={() => setHover(it)} />
          ))}
        </div>

        {/* user */}
        <div className="border-t w-full py-3 flex items-center justify-center" style={{ borderColor: COL.border }}>
          <div
            className="w-9 h-9 rounded-sm flex items-center justify-center text-[10px] font-bold"
            style={{ background: COL.brand, color: "#000", ...fontHead }}
          >
            OM
          </div>
        </div>
      </aside>

      {/* ========== FLYOUT ========== */}
      {hover && FLYOUT_ITEMS[hover.id] && (
        <div
          className="w-56 shrink-0 border-r flex flex-col"
          style={{ borderColor: COL.border, background: COL.surface }}
          onMouseEnter={() => {}}
          onMouseLeave={() => setHover(null)}
        >
          <div className="h-16 flex items-center px-5 border-b" style={{ borderColor: COL.border }}>
            <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: COL.textMuted, ...fontHead }}>
              {hover.label}
            </div>
          </div>
          <div className="flex-1 py-3 px-2 space-y-0.5">
            {FLYOUT_ITEMS[hover.id].map((s, i) => (
              <button
                key={i}
                type="button"
                className="w-full text-left px-3 py-2 rounded-sm text-[13px] transition-colors"
                style={{
                  color: COL.text,
                  background: "transparent",
                  fontFamily: fontBody.fontFamily,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = COL.surfaceHi; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ========== MAIN ========== */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* HEADER */}
        <header
          className="h-16 sticky top-0 z-30 border-b flex items-center justify-between px-6"
          style={{ borderColor: COL.brand, background: COL.surface, borderBottomWidth: 1 }}
        >
          <div className="flex items-center gap-3">
            <Link
              to="/app"
              className="flex items-center gap-1.5 text-[11px] transition-colors uppercase tracking-[0.14em]"
              style={{ color: COL.textMuted, ...fontHead }}
              data-testid="preview-back"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to live app
            </Link>
            <span style={{ color: COL.textDim }}>/</span>
            <span className="text-[11px] uppercase tracking-[0.14em]" style={{ color: COL.text, ...fontHead }}>
              Mockup · Direction 2 · Cockpit
            </span>
          </div>
          <div className="flex items-center gap-3">
            {/* live status */}
            <div className="flex items-center gap-2 px-2.5 py-1 rounded-sm border" style={{ borderColor: COL.border }}>
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: COL.ok }} />
              <span className="text-[10px] uppercase tracking-[0.16em]" style={{ color: COL.textMuted, ...fontHead }}>
                Live · 6 sites
              </span>
            </div>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: COL.textDim }} />
              <input
                placeholder="Search…"
                className="pl-8 pr-3 py-1.5 text-[12px] rounded-sm border outline-none w-56"
                style={{
                  borderColor: COL.border,
                  background: COL.bg,
                  color: COL.text,
                  fontFamily: fontMono.fontFamily,
                }}
              />
            </div>
            <button className="p-2 rounded-sm relative" style={{ background: COL.surfaceHi }}>
              <Bell className="w-4 h-4" style={{ color: COL.textMuted }} />
              <span
                className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full"
                style={{ background: COL.brand, boxShadow: `0 0 6px ${COL.brand}` }}
              />
            </button>
          </div>
        </header>

        {/* SCROLL */}
        <main className="flex-1 overflow-y-auto">
          <div className="px-6 py-5">
            {/* HERO STRIP — site status bar */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-5">
              <KpiCell label="ACTIVE SITES" value="6" trend="+1" trendOk />
              <KpiCell label="WORKERS ON" value="14" trend="14 of 18" trendOk={false} muted />
              <KpiCell label="HOURS TODAY" value="84" suffix="HRS" trend="↗ on pace" trendOk />
              <KpiCell label="HAZARDS · 7D" value="2" trend="↘ 1 vs LW" trendOk />
              <KpiCell label="TS PENDING" value="7" trend="DUE 5PM" warn />
              <KpiCell label="FLEET ALERTS" value="2" trend="WoF · SERVICE" warn pulse />
            </div>

            {/* GREETING */}
            <div className="mb-5">
              <div className="text-[10px] uppercase tracking-[0.24em] mb-1.5" style={{ color: COL.textDim, ...fontHead }}>
                Mon · 22 Feb 2026 · 07:38 NZDT
              </div>
              <h1 className="text-[44px] leading-none uppercase tracking-tight" style={fontHead}>
                <span style={{ color: COL.brand }}>//</span> Cockpit
              </h1>
              <p className="text-[13px] mt-2" style={{ color: COL.textMuted }}>
                Six sites live. Two fleet flags. Tomorrow looks dry — push the Henderson pour.
              </p>
            </div>

            {/* BENTO */}
            <div className="grid grid-cols-12 gap-3">
              {/* setup mission */}
              <SetupMission className="col-span-12 lg:col-span-8" />
              {/* weather */}
              <WeatherCard className="col-span-12 lg:col-span-4" />

              {/* live feed */}
              <LiveFeed className="col-span-12 lg:col-span-7" />
              {/* fleet alerts */}
              <FleetAlerts className="col-span-12 lg:col-span-5" />

              {/* site photo card */}
              <SiteCamCard className="col-span-12 lg:col-span-5" />
              {/* runsheet */}
              <RunSheet className="col-span-12 lg:col-span-7" />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

// ============================================================
function RailItem({ icon: Icon, label, badge, active, onHover }) {
  return (
    <button
      type="button"
      onMouseEnter={onHover}
      className="relative w-10 h-10 my-0.5 rounded-sm flex items-center justify-center transition-all group"
      style={{
        background: active ? COL.brandSoft : "transparent",
        color: active ? COL.brand : COL.textMuted,
        boxShadow: active ? `inset 2px 0 0 ${COL.brand}` : "none",
      }}
      title={label}
    >
      <Icon className="w-[18px] h-[18px]" strokeWidth={1.8} />
      {badge != null && (
        <span
          className="absolute -top-0.5 -right-0.5 text-[8.5px] font-bold tabular-nums px-1 py-px rounded-sm"
          style={{ background: COL.brand, color: "#000", ...fontHead }}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

function Card({ children, className = "", style = {} }) {
  return (
    <div
      className={`rounded-sm border overflow-hidden ${className}`}
      style={{ borderColor: COL.border, background: COL.surface, ...style }}
    >
      {children}
    </div>
  );
}

function KpiCell({ label, value, suffix, trend, trendOk, warn, muted, pulse }) {
  const trendColor = warn ? COL.warn : trendOk ? COL.ok : COL.textMuted;
  return (
    <div
      className="rounded-sm border p-3 relative"
      style={{ borderColor: warn ? COL.warn : COL.border, background: COL.surface }}
    >
      {pulse && (
        <span
          className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full animate-pulse"
          style={{ background: COL.warn }}
        />
      )}
      <div className="text-[9px] uppercase tracking-[0.18em]" style={{ color: COL.textDim, ...fontHead }}>
        {label}
      </div>
      <div className="flex items-baseline gap-1.5 mt-1">
        <div
          className="text-[28px] leading-none tabular-nums"
          style={{ ...fontHead, color: warn ? COL.warn : COL.text }}
        >
          {value}
        </div>
        {suffix && (
          <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: COL.textDim, ...fontHead }}>
            {suffix}
          </div>
        )}
      </div>
      <div className="text-[10px] mt-1 uppercase tracking-[0.12em]" style={{ color: trendColor, ...fontHead }}>
        {trend}
      </div>
    </div>
  );
}

function SetupMission({ className = "" }) {
  const done = 6, total = 7;
  const pct = (done / total) * 100;
  const items = [
    { label: "Upload company logo", done: false, hint: "Appears on every PDF" },
    { label: "Set pay-week ending day", done: true },
    { label: "Invite your team", done: true },
    { label: "Add your first client", done: true },
    { label: "Create your first job", done: true },
    { label: "Add a fleet vehicle", done: true },
    { label: "Add GST + bank details", done: true },
  ];
  return (
    <Card className={className}>
      {/* danger-stripe header */}
      <div
        className="px-5 py-3 flex items-center justify-between border-b"
        style={{ borderColor: COL.border, background: COL.surfaceHi }}
      >
        <div className="flex items-center gap-3">
          <Zap className="w-4 h-4" style={{ color: COL.brand }} />
          <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: COL.brand, ...fontHead }}>
            // Mission · first-week setup
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-[24px] leading-none tabular-nums" style={fontHead}>
            {Math.round(pct)}%
          </div>
          <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: COL.textDim, ...fontHead }}>
            {done}/{total}
          </div>
        </div>
      </div>

      {/* progress */}
      <div className="px-5 pt-4">
        <div className="h-1 rounded-full overflow-hidden" style={{ background: COL.borderHi }}>
          <div
            className="h-full"
            style={{
              width: `${pct}%`,
              background: COL.brand,
              boxShadow: `0 0 12px ${COL.brandGlow}`,
            }}
          />
        </div>
      </div>

      {/* items grid */}
      <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-2">
        {items.map((it, i) => (
          <div
            key={i}
            className="flex items-center gap-3 px-3 py-2.5 rounded-sm border transition-colors"
            style={{
              borderColor: it.done ? COL.border : COL.brand,
              background: it.done ? "transparent" : COL.brandSoft,
            }}
          >
            <div
              className="w-4 h-4 rounded-sm flex items-center justify-center shrink-0"
              style={{
                background: it.done ? COL.brand : "transparent",
                border: `1px solid ${it.done ? COL.brand : COL.brand}`,
                boxShadow: it.done ? `0 0 8px ${COL.brandGlow}` : "none",
              }}
            >
              {it.done && <CheckCircle2 className="w-3 h-3" style={{ color: "#000" }} strokeWidth={3} />}
            </div>
            <div className="flex-1 min-w-0">
              <div
                className="text-[12px] uppercase tracking-[0.04em]"
                style={{
                  color: it.done ? COL.textDim : COL.text,
                  ...fontHead,
                  fontWeight: it.done ? 600 : 800,
                  textDecoration: it.done ? "line-through" : "none",
                }}
              >
                {it.label}
              </div>
              {it.hint && !it.done && (
                <div className="text-[10px] mt-0.5 uppercase tracking-[0.1em]" style={{ color: COL.textMuted, ...fontHead }}>
                  {it.hint}
                </div>
              )}
            </div>
            {!it.done && (
              <span
                className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm"
                style={{ background: COL.brand, color: "#000", ...fontHead }}
              >
                Go →
              </span>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

function WeatherCard({ className = "" }) {
  const days = [
    { d: "MON", t: 21, icon: Sun, ok: true },
    { d: "TUE", t: 19, icon: Cloud, ok: true },
    { d: "WED", t: 14, icon: Wind, ok: false },
    { d: "THU", t: 13, icon: Wind, ok: false },
    { d: "FRI", t: 18, icon: Sun, ok: true },
  ];
  return (
    <Card className={className}>
      <div className="px-5 py-3 border-b flex items-center gap-2" style={{ borderColor: COL.border, background: COL.surfaceHi }}>
        <Activity className="w-3.5 h-3.5" style={{ color: COL.brand }} />
        <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: COL.brand, ...fontHead }}>
          // 5-day · Auckland
        </div>
      </div>
      <div className="p-5">
        <div className="flex items-end gap-2 mb-4">
          <Sun className="w-10 h-10" style={{ color: COL.brand }} strokeWidth={1.5} />
          <div>
            <div className="text-[38px] leading-none tabular-nums" style={fontHead}>21°</div>
            <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: COL.textMuted, ...fontHead }}>
              Partly cloudy
            </div>
          </div>
        </div>
        <div className="grid grid-cols-5 gap-1.5">
          {days.map((d) => {
            const Icon = d.icon;
            return (
              <div
                key={d.d}
                className="text-center py-2 rounded-sm border"
                style={{
                  borderColor: d.ok ? COL.border : COL.warn,
                  background: d.ok ? "transparent" : "rgba(245, 158, 11, 0.08)",
                }}
              >
                <div className="text-[9px] uppercase tracking-[0.1em]" style={{ color: COL.textMuted, ...fontHead }}>
                  {d.d}
                </div>
                <Icon className="w-3.5 h-3.5 mx-auto my-1" style={{ color: d.ok ? COL.text : COL.warn }} />
                <div className="text-[12px] tabular-nums" style={fontHead}>{d.t}°</div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 text-[10px] uppercase tracking-[0.12em] px-2 py-1.5 rounded-sm" style={{ color: COL.warn, background: "rgba(245, 158, 11, 0.08)", ...fontHead }}>
          ⚠ Rain Wed/Thu · shift Henderson pour
        </div>
      </div>
    </Card>
  );
}

function LiveFeed({ className = "" }) {
  const rows = [
    { who: "BEN", what: "submitted timesheet · 8.5h", job: "Britomart kerb", time: "12m", tone: "brand", icon: Clock },
    { who: "MARA", what: "logged hazard · slip risk", job: "Henderson SW", time: "1h", tone: "warn", icon: AlertTriangle },
    { who: "TAMA", what: "pre-start: all clear", job: "Plant SP-04", time: "2h", tone: "ok", icon: ShieldCheck },
    { who: "JESS", what: "uploaded 4 photos", job: "Pakenham ext.", time: "3h", tone: "muted", icon: Camera },
    { who: "OFFICE", what: "approved leave · 3 days", job: "BEN", time: "4h", tone: "muted", icon: FileText },
    { who: "SYSTEM", what: "WoF reminder fired", job: "ABC123 Hilux", time: "5h", tone: "warn", icon: Bell },
  ];
  const dotColor = (t) => t === "brand" ? COL.brand : t === "warn" ? COL.warn : t === "ok" ? COL.ok : COL.textDim;
  return (
    <Card className={className}>
      <div className="px-5 py-3 flex items-center justify-between border-b" style={{ borderColor: COL.border, background: COL.surfaceHi }}>
        <div className="flex items-center gap-2">
          <Activity className="w-3.5 h-3.5" style={{ color: COL.brand }} />
          <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: COL.brand, ...fontHead }}>
            // Live feed
          </div>
        </div>
        <button className="flex items-center gap-1 text-[10px] uppercase tracking-[0.18em]" style={{ color: COL.textMuted, ...fontHead }}>
          See all <ArrowUpRight className="w-3 h-3" />
        </button>
      </div>
      <div>
        {rows.map((r, i) => {
          const Icon = r.icon;
          return (
            <div
              key={i}
              className="flex items-center gap-3 px-5 py-3 border-b"
              style={{ borderColor: COL.border }}
            >
              <div
                className="w-7 h-7 rounded-sm flex items-center justify-center shrink-0"
                style={{ background: COL.surfaceHi, border: `1px solid ${dotColor(r.tone)}` }}
              >
                <Icon className="w-3.5 h-3.5" style={{ color: dotColor(r.tone) }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[12px]" style={fontBody}>
                  <span className="font-bold" style={{ ...fontHead, fontWeight: 700, letterSpacing: "0.04em" }}>{r.who}</span>
                  <span style={{ color: COL.textMuted }}> {r.what}</span>
                </div>
                <div className="text-[10px] uppercase tracking-[0.12em] mt-0.5" style={{ color: COL.textDim, ...fontHead }}>
                  {r.job}
                </div>
              </div>
              <div className="text-[10px] tabular-nums uppercase tracking-[0.16em] shrink-0" style={{ color: COL.textDim, ...fontHead }}>
                {r.time}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function FleetAlerts({ className = "" }) {
  const rows = [
    { rego: "ABC123", asset: "Hilux ute", issue: "WoF in 6d", tone: "warn" },
    { rego: "TR4501", asset: "Tipper", issue: "Service +247km", tone: "warn" },
    { rego: "EX-08", asset: "20T excavator", issue: "No pre-start", tone: "info" },
    { rego: "DG-12", asset: "Digger", issue: "Hour meter due", tone: "warn" },
  ];
  return (
    <Card className={className}>
      <div className="px-5 py-3 flex items-center justify-between border-b" style={{ borderColor: COL.border, background: COL.surfaceHi }}>
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5" style={{ color: COL.warn }} />
          <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: COL.warn, ...fontHead }}>
            // Fleet · 4 to action
          </div>
        </div>
      </div>
      <div className="p-3 space-y-2">
        {rows.map((r, i) => (
          <div
            key={i}
            className="flex items-center justify-between px-3 py-2.5 rounded-sm border"
            style={{
              borderColor: r.tone === "warn" ? COL.warn : COL.border,
              background: r.tone === "warn" ? "rgba(245, 158, 11, 0.06)" : "transparent",
            }}
          >
            <div className="flex items-center gap-3 min-w-0">
              <Truck className="w-4 h-4 shrink-0" style={{ color: r.tone === "warn" ? COL.warn : COL.textMuted }} />
              <div className="min-w-0">
                <div className="text-[12px] tabular-nums" style={{ ...fontHead, letterSpacing: "0.04em" }}>
                  {r.rego}
                </div>
                <div className="text-[10px] uppercase tracking-[0.1em]" style={{ color: COL.textMuted, ...fontHead }}>
                  {r.asset} · {r.issue}
                </div>
              </div>
            </div>
            <button
              className="text-[9px] uppercase tracking-[0.16em] px-2 py-1 rounded-sm"
              style={{ background: COL.surfaceHi, color: COL.text, ...fontHead }}
            >
              Fix →
            </button>
          </div>
        ))}
      </div>
    </Card>
  );
}

function SiteCamCard({ className = "" }) {
  return (
    <Card className={className}>
      <div className="px-5 py-3 flex items-center gap-2 border-b" style={{ borderColor: COL.border, background: COL.surfaceHi }}>
        <Camera className="w-3.5 h-3.5" style={{ color: COL.brand }} />
        <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: COL.brand, ...fontHead }}>
          // Britomart kerb · live
        </div>
        <span className="ml-auto w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: COL.ok }} />
      </div>
      <div
        className="aspect-[16/9] relative"
        style={{
          backgroundImage:
            "url(https://images.pexels.com/photos/1078884/pexels-photo-1078884.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.7) 100%)" }} />
        <div className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-sm" style={{ background: "rgba(0,0,0,0.6)", color: COL.text, ...fontHead }}>
          ● REC · 07:38
        </div>
        <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: COL.textMuted, ...fontHead }}>
              Crew on site
            </div>
            <div className="text-[16px]" style={{ color: COL.text, ...fontHead }}>
              4 / 4 punched in
            </div>
          </div>
          <button className="text-[10px] uppercase tracking-[0.16em] px-2.5 py-1.5 rounded-sm" style={{ background: COL.brand, color: "#000", ...fontHead }}>
            Open site →
          </button>
        </div>
      </div>
    </Card>
  );
}

function RunSheet({ className = "" }) {
  const crew = [
    { name: "BEN", job: "Britomart kerb", role: "Lead", color: COL.brand },
    { name: "MARA", job: "Henderson SW", role: "Operator", color: COL.textMuted },
    { name: "TAMA", job: "Pakenham ext.", role: "Lead", color: COL.textMuted },
    { name: "JESS", job: "Henderson SW", role: "Crew", color: COL.textMuted },
    { name: "OWEN", job: "Pakenham ext.", role: "Crew", color: COL.textMuted },
  ];
  return (
    <Card className={className}>
      <div className="px-5 py-3 flex items-center justify-between border-b" style={{ borderColor: COL.border, background: COL.surfaceHi }}>
        <div className="flex items-center gap-2">
          <Users className="w-3.5 h-3.5" style={{ color: COL.brand }} />
          <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: COL.brand, ...fontHead }}>
            // Tomorrow · run sheet
          </div>
        </div>
        <button className="flex items-center gap-1 text-[10px] uppercase tracking-[0.18em]" style={{ color: COL.textMuted, ...fontHead }}>
          Dispatch <ArrowUpRight className="w-3 h-3" />
        </button>
      </div>
      <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
        {crew.map((c, i) => (
          <div
            key={i}
            className="flex items-center gap-3 px-3 py-2.5 rounded-sm border"
            style={{ borderColor: COL.border, background: COL.surfaceHi }}
          >
            <div
              className="w-9 h-9 rounded-sm flex items-center justify-center text-[10px] shrink-0"
              style={{ background: c.color, color: "#000", ...fontHead }}
            >
              {c.name.slice(0, 2)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px]" style={{ ...fontHead, letterSpacing: "0.04em" }}>
                {c.name}
              </div>
              <div className="text-[10px] uppercase tracking-[0.1em]" style={{ color: COL.textMuted, ...fontHead }}>
                {c.job} · {c.role}
              </div>
            </div>
            <MapPin className="w-3.5 h-3.5 shrink-0" style={{ color: COL.textDim }} />
          </div>
        ))}
      </div>
    </Card>
  );
}
